import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Grid, Typography } from "@mui/material";
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";

import {
  ESTADOS_LLAMADAS_CHOICE,
  EstadosLlamadaEnumChoice,
  formatHour,
  gridSize,
  gridSizeMdLg4,
  gridSizeMdLg6,
  parentescoMockupData,
  texto,
} from "@/shared";

import {
  CustomAutocomplete,
  CustomAutocompleteArrString,
  CustomTextArea,
  CustomTextAreaNoForm,
  CustomTextField,
  CustomTextFieldNoForm,
  SingleButton,
  SingleIconButton,
  TabTexLabelCustomSpace,
  TabTextLabel,
} from "@/shared/components/ui";
import CustomCellphoneTextField from "@/shared/components/ui/CustomCellphoneTextField";
import ImagesWithZoom from "@/shared/components/ui/Images/ImagesWithZoom";
import { IPreagendamiento } from "@/shared/interfaces";
import ServiceAndOthersPartPreagendamientoForm from "./ServiceAndOthersPartPreagendamientoForm";
import UbicacionPreagendamiento from "./UbicacionPreagendamiento";
import { TooltipIconEdit } from "@/erp/sale";
import { SaveFormDataPreagendamiento } from "../../interface/SaveFormDataPreagendamiento";
import { SelectDateTimerSchedulingPyl } from "@/erp/operaciones/agendamiento/shared/components/calendario-preagendamiento-pyl/SelectDateTimerSchedulingPyl";
import useSelectDateTimerPreScheduling from "@/erp/sale/component/calendario-preagendamiento-venta/useSelectDateTimerPreScheduling.store";

export type PreagendamientoSinglePageProps = {
  form: UseFormReturn<SaveFormDataPreagendamiento>;
  shrink?: boolean;
  preagendamiento: IPreagendamiento;
  isCorrectingData?: boolean;
};

const PreagendamientoSinglePage: React.FC<PreagendamientoSinglePageProps> = ({
  form,
  shrink = true,
  isCorrectingData = false,
  preagendamiento,
}) => {
  ///* form --------------------
  const { errors } = form?.formState;

  ///* constants --------------------
  const [canViewDatosServicio, setCanViewDatosServicio] =
    useState<boolean>(false);

  const [isVisibleCalendario, setIsVisibleCalendario] = useState(false)
  const slot = useSelectDateTimerPreScheduling(s => s.slot);
  const setSelectFloat = useSelectDateTimerPreScheduling(s => s.setSelectFloat);
  const setPlanner = useSelectDateTimerPreScheduling(s => s.setPlanner);
  

  /*********** Handlers */
  const onEditHorario = async () => {
    // if cancel edit, set flota to db value

    console.log("onEditHorario", isVisibleCalendario);

    if (isVisibleCalendario) {
      form.reset({
        ...form.getValues(),
        flota: preagendamiento?.flota || "",
        flotaRaw: preagendamiento?.flota_data || {},
      });

      return
    }

    setSelectFloat(preagendamiento?.flota || "");
    setPlanner(preagendamiento?.flota || "");
  };

  return (
    <Grid item container spacing={3}>
      <TabTextLabel text="Datos Personales" />

      {/* =========== personal info =========== */}
      <>
        <CustomTextField
          name="tipo_identificacion"
          label="Tipo de Identificación"
          control={form.control}
          defaultValue={form.getValues().tipo_identificacion || ""}
          error={errors.tipo_identificacion}
          helperText={errors.tipo_identificacion?.message?.toString() ?? ""}
          shrink={shrink}
          size={gridSizeMdLg6}
          disabled
        />
        <CustomTextField
          name="identificacion"
          label="No. Identificación"
          control={form.control}
          defaultValue={form.getValues().identificacion || ""}
          error={errors.identificacion}
          helperText={errors.identificacion?.message?.toString() ?? ""}
          shrink={shrink}
          size={gridSizeMdLg6}
          disabled
        />
        <CustomTextField
          name="razon_social"
          label="Nombre"
          control={form.control}
          defaultValue={form.getValues().razon_social || ""}
          error={errors.razon_social}
          helperText={errors.razon_social?.message?.toString() ?? ""}
          shrink={shrink}
          disabled
        />

        <CustomTextField
          endAdornmentInput={<TooltipIconEdit />}
          name="persona_referencia_nombre"
          label="Persona de Referencia"
          control={form.control}
          defaultValue={form.getValues().persona_referencia_nombre || ""}
          error={errors.persona_referencia_nombre}
          helperText={errors.persona_referencia_nombre?.message}
          shrink={shrink}
          disabled={isCorrectingData}
        />
        <CustomAutocomplete<any>
          endAdornmentInput={<TooltipIconEdit />}
          size={gridSizeMdLg6}
          label={texto.tipo_parentesco}
          name="persona_referencia_parentesco"
          options={parentescoMockupData || []}
          valueKey="nombre" // name is the fk
          defaultValue={form.getValues().persona_referencia_parentesco || ""}
          isLoadingData={false}
          control={form.control}
          error={errors.persona_referencia_parentesco}
          helperText={errors.persona_referencia_parentesco?.message}
        />

        <CustomCellphoneTextField
          endAdornmentInput={<TooltipIconEdit />}
          name="persona_referencia_celular"
          label="Celular Persona Referencia"
          control={form.control}
          defaultValue={form.getValues().persona_referencia_celular || ""}
          error={errors.persona_referencia_celular}
          helperText={errors.persona_referencia_celular?.message}
          shrink={shrink}
          size={gridSizeMdLg6}
          disabled={isCorrectingData}
        />
      </>

      {/* =========== Datos de Venta =========== */}
      <TabTextLabel text="Fotos del Cliente" pt={2} />

      {
        [
          {
            url: preagendamiento?.preventa_data?.url_foto_identificacion_delantera,
            alt: "Identificación Frontal"
          },
          {
            url: preagendamiento?.preventa_data?.url_foto_identificacion_posterior,
            alt: "Identificación Posterior"
          },
          {
            url: preagendamiento?.preventa_data?.url_foto_aceptacion,
            alt: "Foto Aceptación"
          },
          {
            url: preagendamiento?.preventa_data?.url_foto_cuenta_bancaria,
            alt: "Cuenta Bancaria"
          },
          {
            url: preagendamiento?.preventa_data?.url_foto_ruc,
            alt: "RUC"
          },
          {
            url: preagendamiento?.preventa_data?.url_foto_nombramiento,
            alt: "Nombramiento"
          },
        ].filter(e => e.url).map((element) => {

          return <Grid item {...gridSizeMdLg4}>
            <ImagesWithZoom
              src={element.url!}
              alt={element.alt}
            />
            <Typography variant="subtitle2">{element.alt}</Typography>
          </Grid>
        })
      }

      {/* =========== estado llamada =========== */}
      <TabTextLabel text="Estado de la llamada" pt={2} />
      <CustomCellphoneTextField
        endAdornmentInput={<TooltipIconEdit />}
        name="celular"
        label="Celular Cliente"
        control={form.control}
        defaultValue={form.getValues().celular || ""}
        error={errors.celular}
        helperText={errors.celular?.message}
        shrink={shrink}
        size={gridSizeMdLg6}
        disabled={isCorrectingData}
      />
      <CustomCellphoneTextField
        endAdornmentInput={<TooltipIconEdit />}
        name="celular_2"
        label="Celular 2 Cliente"
        control={form.control}
        defaultValue={form.getValues().celular_2 || ""}
        error={errors.celular_2}
        helperText={errors.celular_2?.message}
        shrink={shrink}
        size={gridSizeMdLg6}
        disabled={isCorrectingData}
      />
      {/* --------- estado llamada --------- */}
      <CustomAutocompleteArrString
        label="Estado de la llamada"
        name="estado_llamada"
        control={form.control}
        defaultValue={form.getValues().estado_llamada || ""}
        options={ESTADOS_LLAMADAS_CHOICE.filter(
          (el) => el !== EstadosLlamadaEnumChoice.EN_ESPERA
        )}
        isLoadingData={false}
        error={errors.estado_llamada}
        helperText={errors.estado_llamada?.message}
        disableClearable
        disabled={isCorrectingData}
      />
      <CustomTextArea
        label="Observación Llamada"
        name="observacion_llamada"
        control={form.control}
        defaultValue={
          form.getValues()?.observaciones?.observacion_llamada || ""
        }
        error={errors.observacion_llamada}
        helperText={errors.observacion_llamada?.message}
        required={false}
      />


      <TabTextLabel text="Ubicación" pt={2} />
      <UbicacionPreagendamiento
        form={form}
        preagendamiento={preagendamiento}
        errors={errors}
      />

      <TabTextLabel text="Observación Asesor" pt={2} />


      <CustomTextAreaNoForm
        disabled={true}
        label="Observación 1"
        defaultValue={preagendamiento.preventa_data.observaciones?.observacion_1 ?? '—'}
        value={preagendamiento.preventa_data?.observaciones?.observacion_1 ?? '—'}
        helperText={errors.observacion_llamada?.message}
        required={false}
      />

      <CustomTextAreaNoForm
        disabled={true}
        label="Observación 2"
        defaultValue={preagendamiento.preventa_data.observaciones?.observacion_2 ?? '—'}
        value={preagendamiento.preventa_data?.observaciones?.observacion_2 ?? '—'}
        helperText={errors.observacion_llamada?.message}
        required={false}
      />

      <Grid
        item
        xs={12}
        container
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Grid item>
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              alignItems: "start",
              justifyContent: "start",
              fontSize: ".9rem",
              color: "#6c737fb0",
            }}
          >
            ACTUALIZAR HORARIOS
          </Typography>
        </Grid>
        {slot == null && (
          <SingleIconButton
            startIcon={<EventRepeatIcon />}
            color={isVisibleCalendario ? "error" : "primary"}
            label={isVisibleCalendario ? "Cancelar edición" : "Editar fecha"}
            onClick={() => {
              setIsVisibleCalendario(!isVisibleCalendario);
              onEditHorario();
            }}
          />
        )}
      </Grid>

      {!isVisibleCalendario && (
        <>
          <CustomTextFieldNoForm
            label="Fecha de instalación"
            value={preagendamiento?.fecha_instalacion || "N/A"}
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextFieldNoForm
            label="Hora de instalación"
            value={
              preagendamiento?.hora_instalacion
                ? formatHour(preagendamiento?.hora_instalacion)
                : "N/A"
            }
            size={gridSizeMdLg6}
            disabled
          />
          <CustomTextFieldNoForm
            label="Flota"
            value={form.getValues().flotaRaw?.nombre ?? "N/A"}
            size={gridSize}
            disabled
          />
        </>
      )}


      {
        isVisibleCalendario && (
          <Grid item xs={12}>
            <SelectDateTimerSchedulingPyl />
          </Grid>
        )
      }

      {/* {isVisibleCalendario && (
        <CalendarioPreagendamientoPyl
          cacheKey={CacheKeyCalendarioENUM.KEY_CACHE_PREAGENDAMIENTO_PYL}
          form={form}
        />
      )} */}

      {/* ================ Service ================ */}
      {preagendamiento?.nap && (
        <TabTexLabelCustomSpace
          textContent="Datos de Servicio"
          showCustomRightSpace={true}
          customRightSpace={
            <SingleButton
              newCustomButton
              startIcon={
                canViewDatosServicio ? <ExpandLessIcon /> : <ExpandMoreIcon />
              }
              label={canViewDatosServicio ? "OCULTAR" : "VER MÁS"}
              variant="text"
              onClick={() => {
                setCanViewDatosServicio((prev) => !prev);
              }}
            />
          }
        />
      )}
      {canViewDatosServicio && (
        <ServiceAndOthersPartPreagendamientoForm
          form={form}
          preagendamiento={preagendamiento}
          isCorrectingData={isCorrectingData}
        />
      )}
    </Grid>
  );
};
export default PreagendamientoSinglePage;
